import {
  Component, ElementRef, ViewChild, ViewChildren, QueryList, OnInit, OnDestroy,
  HostListener, ChangeDetectorRef, AfterViewInit
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { URLS } from 'src/app/constants/URLS';
import { environment } from 'src/environments/environment';
import { getAppBasePath } from 'src/app/utils/share-link.utils';
import { ShortsFeedService } from 'src/app/_service/shorts-feed.service';
import { AxinomDrmService } from 'src/app/_service/axinom-drm.service';
import { AuthenticateserviceService } from 'src/app/_service/authenticateservice.service';
import { WhiteLabelService } from 'src/app/_service/white-label.service';
import { DeviceService } from 'src/app/_service/tv-detect.service';
import { CategoryService } from 'src/app/_service/category.service';
import { MycontentserviceService } from 'src/app/_service/mycontentservice.service';
import { CustomControlsService } from 'src/app/_service/custom-controls.service';
import { ApiService } from 'src/app/_service/api.service';
import { getVideoStreamFile } from 'src/app/utils/media-url.utils';
import { VidsPlayerComponent } from '../../sharedComponents/vids-player/vids-player.component';
import { ShareModalComponent } from '../../sharedComponents/share-modal/share-modal.component';
declare const shaka: any;

/**
 * YouTube Shorts-style vertical-swipe player.
 *
 * Route: :channel/:entry/shorts/:id  (?categoryId=<sectionId>  |  ?seriesId=<seriesId>)
 *
 * URL contract:
 *   - :id is the starting Short's vidLibId
 *   - ?seriesId present → short-drama-series mode; feed is the bounded list
 *     of episodes for that series (via ShortsFeedService.loadInitialSeriesEpisodes,
 *     same payload shape as shaka-player's getSeriesData). No pagination —
 *     episode list is fully loaded in one call.
 *   - ?categoryId present → category-paged swipe feed (existing flow).
 *     Loads via ShortsFeedService (uses seeded items if present, else
 *     fetches page 0 from CategoryService.sectionsByCategoryAndChannelId).
 *   - both absent → single-short mode (no swipe). Used for clicks from
 *     search/watchlist/more-like-this where there's no section context.
 *
 * Scroll mechanics: native CSS scroll-snap-type: y mandatory. Each slide is
 * 100vh. Listen to scroll-end to update currentIndex and (re)load the
 * active short into the shared <app-vids-player>. Wheel/keyboard handlers
 * programmatically scrollTo() for desktop/TV. TV D-pad nav added in a
 * follow-up — current build is touch + wheel + keyboard.
 *
 * Pagination: when currentIndex >= items.length - 2, fire loadNextPage()
 * via the service. Stops automatically when service flips noMore=true
 * (backend returned < PAGE_SIZE items, or source is series).
 */
@Component({
  selector: 'app-shorts-player',
  templateUrl: './shorts-player.component.html',
  styleUrls: ['./shorts-player.component.css']
})
export class ShortsPlayerComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef<HTMLElement>;
  @ViewChildren('slide') slides!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('customPlayer', { static: false }) customPlayerRef!: VidsPlayerComponent;

  items: any[] = [];
  currentIndex = 0;
  categoryId: string | null = null;
  seriesId: string | null = null;
  startingShortId: string | number | null = null;
  isLoading = true;
  noMore = false;

  // Media URL of the active short (only one player at a time)
  mediaUrl = '';
  isMuted = true;  // YT-style: start muted to avoid autoplay-block

  // YT-style action state
  userLikes = new Set<number>();   // populated once via getUserLikedVideos
  showMoreSheet = false;            // bottom-sheet visibility
  optionsTargetVideo: any = null;   // video the sheet was opened for
  currentTime = 0;
  duration = 0;
  isPaused = false;
  showPlayIndicator = false;        // brief center icon flash on tap
  isScrubbing = false;
  showFullDescription = false;      // expandable description inside sheet
  readonly channelUrl: any = URLS.channelUrl;

  /** Snapshot of derived sheet values, computed once on open.
   *  Template binds to these instead of calling helpers per CD cycle —
   *  avoids the console.warn flood from getChannelData() when localStorage
   *  doesn't have 'channelData' cached yet. */
  sheetView: {
    title: string;
    channelId: any;
    channelName: string;
    channelAvatar: string;
    isFollowed: boolean;
    followers: string;
    likes: number;
    views: number;
    uploadDate: string;
    uploadYear: string;
    description: string;
    tags: string[];
  } | null = null;

  // Local cache to avoid re-binding the timeupdate listener on every
  // setActiveMedia() call (we'd leak listeners otherwise).
  private boundTimeUpdate: ((e: Event) => void) | null = null;
  private boundLoadedMeta: ((e: Event) => void) | null = null;
  private boundPlay: ((e: Event) => void) | null = null;
  private boundPause: ((e: Event) => void) | null = null;

  private unsubscribe$ = new Subject<void>();
  private scrollSettleTimeout: any = null;
  private wheelLockUntil = 0;
  private readonly NEAR_END_THRESHOLD = 2;
  private readonly postVideos: any = URLS.mediaUrl;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private feed: ShortsFeedService,
    public axinomService: AxinomDrmService,
    private authService: AuthenticateserviceService,
    private whitelabelService: WhiteLabelService,
    private deviceService: DeviceService,
    private categService: CategoryService,
    private mycontentService: MycontentserviceService,
    private notifications: CustomControlsService,
    private apiService: ApiService
  ) {}

  async ngOnInit() {
    if (this.authService.isGuest()) {
      this.router.navigate(['/v'], { replaceUrl: true });
      return;
    }

    // DRM init — same call shaka-player makes; vids-player needs it set
    // up before it can load encrypted streams.
    const userId = localStorage.getItem('userId');
    await this.axinomService.loadDrmData(userId);

    this.startingShortId = this.route.snapshot.paramMap.get('id');
    this.categoryId = this.route.snapshot.queryParamMap.get('categoryId');
    this.seriesId = this.route.snapshot.queryParamMap.get('seriesId');

    // Pull whatever's in the service. movie-thumbnail seeds before nav,
    // so the common path is a hot cache. Cold cache = refresh / shared link.
    const snap = this.feed.getSnapshot();

    if (this.seriesId) {
      // Short-drama-series mode. movie-thumbnail seeds with the tapped
      // episode so the first slide renders instantly; we always top up via
      // loadInitialSeriesEpisodes to fill the rest of the series, then
      // re-apply feedState so currentIndex lands on the right episode in
      // the now-larger items array.
      const hotSeries = snap.items.length > 0 && String(snap.seriesId) === String(this.seriesId);
      if (hotSeries) {
        this.applyFeedState(snap.items);
      }
      this.feed.loadInitialSeriesEpisodes(this.seriesId, this.startingShortId)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(items => {
          if (items.length > 0) this.applyFeedState(items);
        });
    } else {
      const hot = snap.items.length > 0
        && (snap.categoryId === this.categoryId || this.categoryId === null);

      if (hot) {
        this.applyFeedState(snap.items);
      } else if (this.categoryId) {
        this.feed.loadInitialPage(this.categoryId, this.startingShortId)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(items => this.applyFeedState(items));
      } else {
        // Single-short mode with no service hint — we don't have the metadata
        // to render a useful player. Bail back.
        this.isLoading = false;
        this.location.back();
        return;
      }
    }

    // Subscribe for downstream updates (loadNextPage appends).
    this.feed.state$.pipe(takeUntil(this.unsubscribe$)).subscribe(s => {
      this.items = s.items;
      this.noMore = s.noMore;
      this.cdr.detectChanges();
    });

    // Cache the user's like set once. Per-short isLiked checks are O(1).
    this.categService.getUserLikedVideos()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: any) => {
          if (res?.user_likes) this.userLikes = new Set<number>(res.user_likes.map((n: any) => parseInt(n)));
        },
        error: () => { /* silent — like rail just stays unfilled */ }
      });
  }

  ngAfterViewInit(): void {
    // Once the slides are in the DOM, jump to the starting short.
    // Defer so the container has its real height before scrollTo runs.
    setTimeout(() => this.snapTo(this.currentIndex, false), 0);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    clearTimeout(this.scrollSettleTimeout);
    this.detachVideoListeners(this.customPlayerRef?.videoElement);
    this.feed.clear();
  }

  // ----- Feed wiring -------------------------------------------------------

  private applyFeedState(items: any[]) {
    this.items = items || [];
    const startIdx = this.items.findIndex(
      i => String(i?.vidLibId) === String(this.startingShortId)
    );
    this.currentIndex = startIdx >= 0 ? startIdx : 0;
    this.setActiveMedia(this.currentIndex);
    this.isLoading = false;
    // If the view is already initialized (e.g., a later cold-fetch grows
    // items and shifts currentIndex onto a non-zero slide), scroll the
    // container so the visual position matches. On initial mount the
    // container isn't ready yet — ngAfterViewInit handles that snap.
    if (this.scrollContainer && this.currentIndex > 0) {
      setTimeout(() => this.snapTo(this.currentIndex, false), 0);
    }
  }

  private setActiveMedia(index: number) {
    const video = this.items[index];
    if (!video) {
      this.mediaUrl = '';
      return;
    }
    // Same URL build as shaka-player: HLS on Safari/Apple, DASH+DRM elsewhere.
    const suffix = this.isSafariApple() ? '_master.m3u8' : '_drm_meta.mpd';
    const path = video.playback_path
      ? video.playback_path
      : getVideoStreamFile(video, suffix);
    this.mediaUrl = `${this.postVideos}${path}`;
    this.cdr.detectChanges();
    // vids-player won't load the stream from [src] alone — it exposes an
    // initShakaPlayer(mediaUrl, drmConfig) method that we have to call
    // imperatively, exactly like ShakaPlayerComponent.initPlayer does.
    // ViewChild may not be ready in the same tick, so retry until present.
    this.initVidsPlayerWhenReady();
  }

  private initVidsPlayerWhenReady(retries = 30) {
    if (!this.customPlayerRef) {
      if (retries <= 0) return;
      setTimeout(() => this.initVidsPlayerWhenReady(retries - 1), 100);
      return;
    }
    const drmConfig = {
      servers: {
        'com.widevine.alpha': this.axinomService.getWidevineUrl()
      },
      advanced: {
        'com.widevine.alpha': { persistentStateRequired: true }
      }
    };
    this.customPlayerRef.initShakaPlayer(this.mediaUrl, drmConfig)
      .then(() => {
        const videoEl = this.customPlayerRef.videoElement;
        if (videoEl) {
          videoEl.muted = this.isMuted;
          this.attachVideoListeners(videoEl);
          videoEl.play().catch(err => console.warn('Shorts autoplay blocked:', err));
        }
      })
      .catch(err => console.error('Shorts initShakaPlayer error:', err));
  }

  /**
   * Bind seek-bar + play-state listeners to the active <video> element.
   * Detach prior listeners first so swapping slides doesn't leak.
   */
  private attachVideoListeners(videoEl: HTMLVideoElement) {
    this.detachVideoListeners(videoEl);
    this.currentTime = 0;
    this.duration = 0;
    this.isPaused = false;
    // YT-Shorts behavior: loop the current short forever. User must swipe
    // up/down to navigate — no auto-advance on end.
    videoEl.loop = true;

    this.boundTimeUpdate = () => {
      if (this.isScrubbing) return;
      this.currentTime = videoEl.currentTime || 0;
      this.cdr.detectChanges();
    };
    this.boundLoadedMeta = () => {
      this.duration = videoEl.duration || 0;
      this.cdr.detectChanges();
    };
    this.boundPlay = () => { this.isPaused = false; this.cdr.detectChanges(); };
    this.boundPause = () => { this.isPaused = true; this.cdr.detectChanges(); };

    videoEl.addEventListener('timeupdate', this.boundTimeUpdate);
    videoEl.addEventListener('loadedmetadata', this.boundLoadedMeta);
    videoEl.addEventListener('durationchange', this.boundLoadedMeta);
    videoEl.addEventListener('play', this.boundPlay);
    videoEl.addEventListener('pause', this.boundPause);
  }

  private detachVideoListeners(videoEl: HTMLVideoElement | null | undefined) {
    if (!videoEl) return;
    if (this.boundTimeUpdate) videoEl.removeEventListener('timeupdate', this.boundTimeUpdate);
    if (this.boundLoadedMeta) {
      videoEl.removeEventListener('loadedmetadata', this.boundLoadedMeta);
      videoEl.removeEventListener('durationchange', this.boundLoadedMeta);
    }
    if (this.boundPlay) videoEl.removeEventListener('play', this.boundPlay);
    if (this.boundPause) videoEl.removeEventListener('pause', this.boundPause);
  }

  private maybeFetchMore() {
    if (this.noMore) return;
    if (this.items.length - this.currentIndex <= this.NEAR_END_THRESHOLD) {
      this.feed.loadNextPage().pipe(takeUntil(this.unsubscribe$)).subscribe();
    }
  }

  // ----- Scroll-snap → currentIndex sync -----------------------------------

  onScroll() {
    clearTimeout(this.scrollSettleTimeout);
    // Debounce — fire only after scrolling stops for ~120ms (snap settle).
    this.scrollSettleTimeout = setTimeout(() => this.handleScrollSettle(), 120);
  }

  private handleScrollSettle() {
    if (!this.scrollContainer?.nativeElement) return;
    const container = this.scrollContainer.nativeElement;
    const idx = Math.round(container.scrollTop / container.clientHeight);
    if (idx !== this.currentIndex && idx >= 0 && idx < this.items.length) {
      this.currentIndex = idx;
      this.setActiveMedia(idx);
      this.maybeFetchMore();
    }
  }

  next() { this.snapTo(this.currentIndex + 1, true); }
  prev() { this.snapTo(this.currentIndex - 1, true); }

  private snapTo(index: number, smooth: boolean) {
    if (index < 0 || index >= this.items.length) return;
    const container = this.scrollContainer?.nativeElement;
    if (!container) return;
    container.scrollTo({
      top: index * container.clientHeight,
      behavior: smooth ? 'smooth' : 'auto'
    });
    // The scroll handler will sync currentIndex once snap settles; for
    // smooth keyboard/wheel we update immediately so the active player
    // swaps without a visible flash.
    this.currentIndex = index;
    this.setActiveMedia(index);
    this.maybeFetchMore();
  }

  // ----- Input handlers ----------------------------------------------------

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent) {
    // Throttle: trackpad emits many wheel events per real swipe gesture.
    const now = Date.now();
    if (now < this.wheelLockUntil) {
      event.preventDefault();
      return;
    }
    if (Math.abs(event.deltaY) < 10) return;
    event.preventDefault();
    this.wheelLockUntil = now + 600;
    if (event.deltaY > 0) this.next();
    else this.prev();
  }

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') { event.preventDefault(); this.next(); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); this.prev(); }
    else if (event.key === 'Escape') { this.goBack(); }
    else if (event.key === ' ') { event.preventDefault(); /* toggle handled by tap */ }
  }

  // ----- UI actions --------------------------------------------------------

  toggleMute() {
    this.isMuted = !this.isMuted;
  }

  goBack() {
    this.location.back();
  }

  onVideoEnded() {
    // Fallback for browsers/DRM paths that don't honor videoEl.loop.
    // Restart the current short rather than advancing — YT-Shorts behavior.
    const videoEl = this.customPlayerRef?.videoElement;
    if (videoEl) {
      videoEl.currentTime = 0;
      videoEl.play().catch(() => {});
    }
  }

  onBackFromPlayer() {
    this.goBack();
  }

  // ----- Helpers -----------------------------------------------------------

  trackByVidLibId = (_: number, item: any) => item?.vidLibId;

  getPoster(video: any): string {
    if (!video) return '';
    const id = video.vidLibId || video.mediaId;
    return id ? `${this.postVideos}VID_${id}_thumb_v.jpg` : '';
  }

  /** Handle both raw section-payload shape (channel_desc inside properties[])
   *  and pre-transformed shape (channel_desc at top level). */
  getChannelName(video: any): string {
    if (!video) return '';
    if (video.channel_desc) return video.channel_desc;
    if (Array.isArray(video.properties)) {
      return video.properties.find((p: any) => p.name === 'channel_desc')?.value || '';
    }
    return '';
  }

  /** Same dual-shape handling for title — raw section data nests it in
   *  properties[name='title'], transformed data has it at top level. */
  getTitle(video: any): string {
    if (!video) return '';
    if (video.title) return video.title;
    if (Array.isArray(video.properties)) {
      return video.properties.find((p: any) => p.name === 'title')?.value || '';
    }
    return '';
  }

  /**
   * Linked full-length video id (relatedVideoId) — same dual-shape handling as
   * getTitle. Returns 0 / null-ish as null so the template can use a truthy
   * check without showing the CTA on cleared/unset values.
   */
  getRelatedVideoId(video: any): number | null {
    if (!video) return null;
    let raw: any = video.relatedVideoId;
    if (raw == null && Array.isArray(video.properties)) {
      raw = video.properties.find((p: any) =>
        p.name === 'relatedVideoId' || p.name === 'related_video_id'
      )?.value;
    }
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Navigate to the linked full-length video's detail page. Uses the movies
   * route (`/movies/:title/:vidLibId`) — the title segment is cosmetic; the
   * video-details component fetches by vidLibId. If the target turns out to
   * be a series episode, video-details follows the existing series_id branch.
   */
  openRelatedVideo(video: any, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    const relatedId = this.getRelatedVideoId(video);
    if (!relatedId) return;
    const route = this.whitelabelService.getPrefixedRoute('movies', 'related', String(relatedId));
    this.router.navigate([route]);
  }

  isSafariApple(): boolean {
    const ua = navigator.userAgent;
    return /Macintosh|iPad|iPhone|iPod/.test(ua) && /^((?!chrome|android).)*safari/i.test(ua);
  }

  isTV(): boolean {
    return this.deviceService.isTV();
  }

  // ----- Tap-to-pause -------------------------------------------------------

  /** Tap the player area to toggle pause; brief center-icon flash on toggle. */
  togglePlayPause() {
    const videoEl = this.customPlayerRef?.videoElement;
    if (!videoEl) return;
    if (videoEl.paused) {
      videoEl.play().catch(() => {});
    } else {
      videoEl.pause();
    }
    this.showPlayIndicator = true;
    setTimeout(() => {
      this.showPlayIndicator = false;
      this.cdr.detectChanges();
    }, 400);
  }

  // ----- Like ---------------------------------------------------------------

  isLiked(video: any): boolean {
    return !!video && this.userLikes.has(Number(video.vidLibId));
  }

  toggleLike(video: any, event: Event) {
    event.stopPropagation();
    if (!video?.vidLibId) return;
    const id = Number(video.vidLibId);
    const userId = localStorage.getItem('userId');
    const currentlyLiked = this.userLikes.has(id);
    // Optimistic flip
    if (currentlyLiked) this.userLikes.delete(id); else this.userLikes.add(id);
    this.categService.likeDislikeContent(video.vidLibId, userId, currentlyLiked ? 'none' : 'liked')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        error: () => {
          // Revert on failure
          if (currentlyLiked) this.userLikes.add(id); else this.userLikes.delete(id);
          this.notifications.showNotification('error', 'Could not update like');
        }
      });
  }

  // ----- Share --------------------------------------------------------------

  openShare(video: any, event?: Event) {
    if (event) event.stopPropagation();
    if (!video?.vidLibId) return;
    const host = window.location.hostname;
    const isDev = host.includes('dev.toqqer.com');
    const bPath = isDev ? 'cms' : 'cms-api';
    let videoLink = `${environment.baseUrl}${bPath}/s/${video.vidLibId}`;
    const params: string[] = [];
    if (this.whitelabelService.isWhiteLabel()) {
      const channel = (this.whitelabelService.channel || '').replace('@', '');
      const entry = this.whitelabelService.entryType || 'f';
      params.push(`c=${channel}`, `e=${entry}`);
    }
    const basePath = getAppBasePath();
    if (basePath) params.push(`p=${encodeURIComponent(basePath)}`);
    if (params.length) videoLink += '?' + params.join('&');

    if ((navigator as any).share) {
      (navigator as any).share({
        title: video?.title || 'Check out this short',
        url: videoLink
      }).catch(() => {});
    } else {
      this.dialog.open(ShareModalComponent, { data: { videoLink } });
    }
  }

  // ----- Options bottom sheet ----------------------------------------------

  openOptionsSheet(video: any, event: Event) {
    event.stopPropagation();
    this.optionsTargetVideo = video;
    this.showFullDescription = false;
    this.sheetView = video ? this.buildSheetView(video) : null;
    this.showMoreSheet = true;
  }

  closeOptionsSheet() {
    this.showMoreSheet = false;
    this.optionsTargetVideo = null;
    this.sheetView = null;
    this.showFullDescription = false;
  }

  private buildSheetView(v: any) {
    // Section payloads carry the raw shape (most fields buried in a
    // properties[name,value] array). transformVideoData flattens them to
    // the same shape cc-prime/video-details consume, and also formats
    // uploadedTime → "Today"/"N months ago" and extracts the year.
    const flat = v?.properties ? this.mycontentService.transformVideoData(v, 'shorts') : v;
    const hasChannel = !!flat?.channelId;
    // Prefer genres (real taxonomy) over the raw `tag` label for hashtags.
    const hashtagSource: string[] = Array.isArray(flat?.genres) && flat.genres.length
      ? flat.genres
      : (flat?.tags ? String(flat.tags).split(',') : []);
    return {
      title: flat?.title || '',
      channelId: flat?.channelId ?? null,
      channelName: flat?.channel_desc || 'Unknown channel',
      channelAvatar: this.channelAvatarUrl(flat),
      isFollowed: hasChannel ? this.categService.isUserFollowingChannel(flat.channelId) : false,
      followers: hasChannel ? (this.categService.getChannelFollowers(flat.channelId) || '0') : '0',
      likes: flat?.vidLike ?? flat?.likes ?? 0,
      views: flat?.views || 0,
      uploadDate: flat?.uploadedTime || '—',     // already relative-formatted by transformVideoData
      uploadYear: flat?.year ? String(flat.year) : '',
      description: flat?.description || '',
      tags: hashtagSource.map((t: string) => t.trim()).filter(Boolean)
    };
  }

  // ----- Details panel inside the sheet ------------------------------------

  toggleDescription(event?: Event) {
    if (event) event.stopPropagation();
    this.showFullDescription = !this.showFullDescription;
  }

  getDescription(video: any): string {
    return video?.description || video?.synopsis || '';
  }

  channelAvatarUrl(video: any): string {
    const id = video?.channelId;
    if (!id) return './assets/img/profile_avatar.png';
    return `${this.channelUrl}CH_${id}_thumb.jpg`;
  }

  isUserFollowed(video: any): boolean {
    if (!video?.channelId) return false;
    return this.categService.isUserFollowingChannel(video.channelId);
  }

  getChannelFollowers(video: any): string {
    if (!video?.channelId) return '0';
    return this.categService.getChannelFollowers(video.channelId) || '0';
  }

  /** Relative date string like "9 months ago" or "Today". Uses the same
   *  helper cc-prime-player and the rest of the app rely on. */
  getUploadDateText(video: any): string {
    if (!video?.uploadedTime) return '—';
    const text = this.categService.calculateModifiedDate(video.uploadedTime);
    return text || '—';
  }

  getUploadYear(video: any): string {
    if (!video?.uploadedTime) return '';
    const year = this.mycontentService.extractYear(video.uploadedTime);
    return year && !isNaN(year) ? String(year) : '';
  }

  toggleFollow(event: Event) {
    event.stopPropagation();
    if (!this.sheetView?.channelId) return;
    const channelId = this.sheetView.channelId;
    const userId = localStorage.getItem('userId');
    const wasFollowed = this.sheetView.isFollowed;
    // Optimistic flip on the snapshot so the button updates instantly.
    this.sheetView.isFollowed = !wasFollowed;
    this.apiService.post(
      `${URLS.followTimeLineUser}?tqr_user_id=${userId}&channel_id=${channelId}&mute=${wasFollowed}`,
      {}
    ).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => {
        this.notifications.showNotification(
          'success',
          wasFollowed ? 'Unfollowed channel' : 'Following channel'
        );
      },
      error: () => {
        if (this.sheetView) this.sheetView.isFollowed = wasFollowed;  // revert
        this.notifications.showNotification('error', 'Could not update follow');
      }
    });
  }

  isInWatchlist(video: any): boolean {
    return !!video?.is_in_watchlist;
  }

  toggleWatchlist(video: any) {
    if (!video?.vidLibId) return;
    const userId = localStorage.getItem('userId');
    const inList = this.isInWatchlist(video);
    const obs = inList
      ? this.mycontentService.removeFromWatchList(userId, video.vidLibId)
      : this.mycontentService.addInWatchList(userId, video.vidLibId);
    obs.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => {
        video.is_in_watchlist = !inList;
        this.notifications.showNotification('success',
          inList ? 'Removed from watchlist' : 'Added to watchlist');
      },
      error: () => this.notifications.showNotification('error', 'Action failed')
    });
    this.closeOptionsSheet();
  }

  shareFromSheet() {
    const v = this.optionsTargetVideo;
    this.closeOptionsSheet();
    this.openShare(v);
  }

  notInterested() {
    this.notifications.showNotification('success', 'Thanks — we\'ll show fewer like this');
    this.closeOptionsSheet();
  }

  reportShort() {
    this.notifications.showNotification('success', 'Reported. Thanks for the feedback.');
    this.closeOptionsSheet();
  }

  // ----- Seek bar -----------------------------------------------------------

  get progressPercent(): number {
    if (!this.duration || this.duration <= 0) return 0;
    return Math.min(100, (this.currentTime / this.duration) * 100);
  }

  onSeekStart(event: MouseEvent | TouchEvent) {
    event.stopPropagation();
    this.isScrubbing = true;
    this.updateSeekFromEvent(event);
  }

  onSeekMove(event: MouseEvent | TouchEvent) {
    if (!this.isScrubbing) return;
    event.preventDefault();
    this.updateSeekFromEvent(event);
  }

  onSeekEnd(event: MouseEvent | TouchEvent) {
    if (!this.isScrubbing) return;
    event.stopPropagation();
    this.updateSeekFromEvent(event);
    const videoEl = this.customPlayerRef?.videoElement;
    if (videoEl && this.duration > 0) {
      videoEl.currentTime = this.currentTime;
    }
    this.isScrubbing = false;
  }

  private updateSeekFromEvent(event: MouseEvent | TouchEvent) {
    const target = event.currentTarget as HTMLElement;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const clientX = (event as TouchEvent).touches?.[0]?.clientX
      ?? (event as TouchEvent).changedTouches?.[0]?.clientX
      ?? (event as MouseEvent).clientX;
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    this.currentTime = percent * (this.duration || 0);
  }
}
